// Text the legacy pages formatted in C# — kept identical.

const cad = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });
const formats = new Map<string, Intl.NumberFormat>();

/** The same format with another currency's own sign ("€1,234.56"); the dollar format when the code is not one. */
function formatFor(currency: string | null | undefined): Intl.NumberFormat {
  const code = currency?.trim().toUpperCase() ?? '';
  if (!/^[A-Z]{3}$/.test(code) || code === 'CAD') return cad;
  let format = formats.get(code);
  if (!format) {
    try {
      format = new Intl.NumberFormat('en-CA', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' });
    } catch {
      format = cad; // three letters that are no currency
    }
    formats.set(code, format);
  }
  return format;
}

/**
 * decimal.ToString("C2", en-CA): "$1,234.56", "-$1.00"; "--" for no value. The legacy pages printed a dollar sign
 * whatever the card's currency (W10): with the currency given, an amount carries that currency's sign.
 */
export function money(value: number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  return formatFor(currency).format(value).replace('US$', '$');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * DateTimeOffset.ToString("MMM d, yyyy") of an API timestamp ("2026-09-01T10:00:00+07:00"): the date in the
 * timestamp's OWN offset (not the browser's zone), as the server printed it. "2026-09-01" (DateOnly) too.
 */
export function shortDate(iso: string | null | undefined): string | null {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  if (!m) return null;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

/** "MMM d, yyyy h:mm tt" in UTC (the admin pages show UTC times). */
export function dateTimeUtc(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const h = d.getUTCHours();
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()} ${hour12}:${minutes} ${h < 12 ? 'AM' : 'PM'}`;
}

/** The first word of a display name ("there" when none) — the greetings. */
export const firstName = (name: string | null | undefined, fallback = 'there') =>
  name?.split(' ').find((p) => p !== '') ?? fallback;

/** Up to two initials, upper-case — the avatar. */
export const initials = (name: string | null | undefined) =>
  (name ?? '')
    .split(' ')
    .filter((p) => p !== '')
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

/** An enum name as words: "PendingOnboarding" -> "Pending onboarding". */
export const words = (name: string) =>
  name.replace(/([a-z0-9])([A-Z])/g, (_, a: string, b: string) => `${a} ${b.toLowerCase()}`);
